import RawData from '../data/PetSpecies.json';
import { duration } from '../utils/duration';
import { exhaustiveTuple, tuple } from '../utils/types';
import { FateState } from '../fate/fate.ruleset';
import {
  PetSpeciesID,
  PetEvolutionStage,
  PetSpecies as PetSpeciesItems,
} from 'src/replicant/data/PetSpecies.schema';
import { assertNever } from '../utils';

export type PetSpeciesBucket = 'control';

export type { PetEvolutionStage, PetSpeciesID };

export type PetState = (typeof petStates)[number];

export type PetAttribute = (typeof petAttributes)[number];

// Should be manually updated when new species are added.
export const adultSpecies = tuple(
  'giraffe',
  'dinosaur',
  'whale',
  'pinkdog',
  'panda',
  'lion',
  'elephant',
  'reindeer',
  'polarbear',
  'alpaca',
  'tiger',
  'flamingo',
  'cow',
  'persiancat',
  'dolphin',
);

const PetSpecies = RawData as PetSpeciesItems;

export const PetEvolutionMap: Record<
  PetSpeciesBucket,
  Record<PetSpeciesID, PetEvolution>
> = {
  control: PetSpecies.reduce(
    (acc, data) => ({ ...acc, [data.id]: data }),
    {} as Record<PetSpeciesID, PetEvolution>,
  ),
};

export type AdultSpecies = Extract<PetSpeciesID, (typeof adultSpecies)[number]>;
export type StarterSpecies = Exclude<PetSpeciesID, AdultSpecies>;

export type PetEvolution = (typeof PetSpecies)[number];

type AttributeDecayValues = Record<PetSpeciesID, Record<PetAttribute, number>>;

export type PetStatusEffect = (typeof petStatusEffects)[number];

export const petStates = [
  'dead',
  'sleeping',
  'dying',
  'evolving',
  'want_to_go_out',
  'want_to_add_friend',
  'sick',
  'tooth_decay',
  'scared',
  'very_dirty',
  'very_hungry',
  'very_lonely',
  'very_pee',
  'very_sad',
  'dirty',
  'hungry',
  'lonely',
  'pee',
  'sad',
  'happy',
  'normal',
] as const;

export function getPetState(
  attributes: Record<PetAttribute, number>,
  statusEffects: Partial<Record<PetStatusEffect, boolean>>,
  isVisiting: boolean,
): PetState {
  switch (true) {
    case statusEffects.dead:
      return 'dead';
    case statusEffects.asleep:
      return 'sleeping';
    case statusEffects.dying:
      return 'dying';
    case statusEffects.sick:
      return 'sick';
    case statusEffects.evolving && !isVisiting:
      return 'evolving';
    case statusEffects.wantToGoOut && !isVisiting:
      return 'want_to_go_out';
    case statusEffects.wantToInvite && !isVisiting:
      return 'want_to_add_friend';
    case statusEffects.toothDecay:
      return 'tooth_decay';
    case statusEffects.scared:
      return 'scared';
    case attributes.dirtiness > 70:
      return 'very_dirty';
    case attributes.stomach < 15:
      return 'very_hungry';
    case attributes.love < 15:
      return 'very_lonely';
    case attributes.urine > 85:
      return 'very_pee';
    case attributes.dirtiness > 40:
      return 'dirty';
    case attributes.stomach < 40:
      return 'hungry';
    case attributes.love < 40:
      return 'lonely';
    case attributes.urine > 60:
      return 'pee';
    case getStressValue(attributes) < 15:
      return 'happy';
    default:
      return 'normal';
  }
}

export function getPetStateFromFate(fate: FateState): PetState {
  switch (fate) {
    case 'asleep':
      return 'sleeping';
    case 'wantToGoOut':
      return 'want_to_go_out';
    case 'wantToInvite':
      return 'want_to_add_friend';
    case 'toothDecay':
      return 'tooth_decay';
    case 'hadVisitor':
    case 'daycare':
      return 'normal';
    default:
      return fate;
  }
}

export const petAttributes = [
  'stomach',
  'love',
  'dirtiness',
  'urine',
  'toothAcidity',
] as const;

export const attributeBounds = {
  min: 0,
  max: 100,
};

export const getStressValue = ({
  stomach,
  love,
  dirtiness,
  urine,
}: {
  stomach: number;
  love: number;
  dirtiness: number;
  urine: number;
}): number =>
  ((attributeBounds.max - Math.min(stomach, attributeBounds.max)) * 4 +
    (attributeBounds.max - Math.min(love, attributeBounds.max)) * 3 +
    dirtiness * 5 +
    urine * 3) /
  15;

export const nextSpeciesWeights: Record<
  PetSpeciesBucket,
  Record<PetSpeciesID, Record<PetSpeciesID, number>>
> = {
  control: PetSpecies.reduce(
    (acc, data) => ({ ...acc, [data.id]: data.next_weights }),
    {},
  ) as Record<PetSpeciesID, Record<PetSpeciesID, number>>,
};

export const decayIntervals: Record<PetSpeciesBucket, AttributeDecayValues> = {
  control: PetSpecies.reduce((acc, data) => {
    acc[data.id] = toAttributeDecayMap(data);
    return acc;
  }, {} as AttributeDecayValues),
};

export function toAttributeDecayMap(data: {
  dirtiness_sec: number;
  love_sec: number;
  stomach_sec: number;
  tooth_acidity_sec: number;
  urine_sec: number;
}) {
  return {
    dirtiness: duration({ seconds: data.dirtiness_sec }),
    love: duration({
      seconds: data.love_sec,
    }),
    stomach: duration({
      seconds: data.stomach_sec,
    }),
    toothAcidity: duration({ seconds: data.tooth_acidity_sec }),
    urine: duration({ seconds: data.urine_sec }),
  };
}

export const decayMultipliers: Record<PetAttribute, number> = {
  love: -1,
  urine: 1,
  stomach: -1,
  dirtiness: 1,
  toothAcidity: -1,
};

export const decayFriendsCareBoost: Record<PetAttribute, number> = {
  love: 5,
  urine: 0,
  stomach: 5,
  dirtiness: 0,
  toothAcidity: 0,
};

export const petStatusEffects = [
  'dead',
  'asleep',
  'dying',
  'evolving',
  'hadVisitor',
  'sick',
  'toothDecay',
  'scared',
  'wantToGoOut',
  'wantToInvite',
  'daycare',
] as const;
export const shouldGetSick = (stress: number, random: number) =>
  random < stress / 2000;

export const shouldGetToothDecay = (toothAcidity: number, random: number) =>
  random < toothAcidity / 1000;

export const scaredCoefficients: Record<
  PetSpeciesBucket,
  Record<PetSpeciesID, number>
> = {
  control: PetSpecies.reduce(
    (acc, data) => {
      acc[data.id] = data.scared_coefficient;
      return acc;
    },
    {} as Record<PetSpeciesID, number>,
  ),
};

export const shouldGetScared = (
  bucket: PetSpeciesBucket,
  species: PetSpeciesID | undefined,
  love: { current: number; max: number },
  random: number,
) =>
  species &&
  random <
    (scaredCoefficients[bucket][species] * (love.max - love.current)) / 1000;

export const shouldWantToGoOut = (timeSinceLastVisit: number, random: number) =>
  random < wantToGoOutProbability(timeSinceLastVisit);

export const wantToGoOutProbability = (timeSinceLastVisit: number) =>
  (0.0001 * timeSinceLastVisit) / duration({ minutes: 1 });

export function getInviteMultiplier(friendCount: number): number {
  if (friendCount === 0) return 5;
  if (friendCount <= 2) return 3;
  if (friendCount <= 4) return 2;
  return 1;
}

/**
 * A/B 0468: minimum awake-time that must elapse since the last invite nudge
 * before the probability roll is allowed to fire again.
 *
 * 0440's multiplier-only system (5×/3×/2×/1×) only produced a ~13% real-world
 * frequency spread — users with 5+ friends still received ~35 nudges/29d vs
 * ~38–40 for 0-friend users. The cooldown floor creates real separation by
 * hard-blocking the probability roll until the floor passes.
 */
export function getInviteCooldownFloor(friendCount: number): number {
  if (friendCount <= 2) return duration({ hours: 24 });
  if (friendCount <= 4) return duration({ hours: 72 });
  return duration({ hours: 168 });
}

export const wantToInviteProbability = (
  timeSinceLastInvite: number,
  friendCount: number,
) =>
  (getInviteMultiplier(friendCount) * 0.0001 * timeSinceLastInvite) /
  duration({ minutes: 1 });

export const shouldWantToInvite = (
  timeSinceLastInvite: number,
  random: number,
  friendCount: number,
) => random < wantToInviteProbability(timeSinceLastInvite, friendCount);

export const evolutions: Record<
  PetSpeciesBucket,
  Record<PetSpeciesID, PetEvolution>
> = {
  control: PetSpecies.reduce(
    (acc, data) => {
      acc[data.id] = data;
      return acc;
    },
    {} as Record<PetSpeciesID, PetEvolution>,
  ),
};

export const species = Array.from(new Set(PetSpecies.map(({ id }) => id)));

export const petSpeciesTuple = tuple(...species);
export const evolutionSequence = exhaustiveTuple<PetEvolutionStage>()(
  'baby',
  'kid',
  'youth',
  'adult',
);

export const timeToEvolve = {
  baby: duration({ hours: 1 }),
  kid: duration({ hours: 14 }),
  youth: duration({ hours: 24 }),
};

/**
 * Get growth stage from species.
 */
export function getEvolutionStage(
  bucket: PetSpeciesBucket,
  species: PetSpeciesID,
): PetEvolutionStage {
  return PetEvolutionMap[bucket][species].stage;
}

/**
 * Get pet species config from bucket.
 * @param bucket
 * @returns
 */
export function getPetSpeciesById(bucket: PetSpeciesBucket): PetSpeciesItems {
  switch (bucket) {
    case 'control':
      return PetSpecies;

    default:
      assertNever(bucket);
  }
}

/**
 * The gem cost of redoing evolution, based on the amount of times it has been done.
 * Last value will be used for all subsequent evolutions.
 */
export const redoEvolutionCost = [90, 150, 200, 300];

/**
 * Name length limit.
 */
export const NAME_LENGTH_CONSTRAINTS = [1, 10];
